import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * Jumlah item yang menunggu tindakan pengelola pada menu Approval:
 *   - approval_requests berstatus PENDING pada cabang, DAN
 *   - withdrawal_requests berstatus PENDING milik nasabah cabang (alur
 *     penarikan langsung / legacy).
 *
 * Berlangganan Supabase Realtime (migrasi 0007 & 0027) dan menghitung ulang
 * setiap ada perubahan. RLS membatasi payload ke cabang yang berhak.
 *
 * @param branchId Cabang pengelola. `null`/kosong → hook nonaktif, mengembalikan 0.
 */
export function usePendingApprovalCount(branchId: string | null | undefined): number {
  const [count, setCount] = useState(0);

  const refetch = useCallback(async () => {
    if (!branchId) {
      setCount(0);
      return;
    }
    try {
      const [approvals, withdrawals] = await Promise.all([
        supabase
          .from('approval_requests')
          .select('id', { count: 'exact', head: true })
          .eq('branch_id', branchId)
          .eq('status', 'PENDING'),
        supabase
          .from('withdrawal_requests')
          .select('id, nasabah_profiles!inner(branch_id)', { count: 'exact', head: true })
          .eq('status', 'PENDING')
          .eq('nasabah_profiles.branch_id', branchId),
      ]);
      setCount((approvals.count ?? 0) + (withdrawals.count ?? 0));
    } catch {
      // Diamkan: badge bersifat kosmetik, jangan ganggu UI utama.
    }
  }, [branchId]);

  useEffect(() => {
    if (!branchId) {
      setCount(0);
      return undefined;
    }

    refetch();

    const channel = supabase
      .channel(`pengelola-approvals-${branchId}`)
      // approval_requests punya branch_id → filter langsung.
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'approval_requests', filter: `branch_id=eq.${branchId}` },
        () => refetch(),
      )
      // withdrawal_requests tanpa kolom branch_id → dengar semua; RLS membatasi
      // payload ke cabang yang berhak, dan refetch tetap dihitung per cabang.
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'withdrawal_requests' },
        () => refetch(),
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [branchId, refetch]);

  return count;
}

export default usePendingApprovalCount;
